import { useEffect, useRef, useState } from "react";
import type { AxisOrientationAnalysis } from "@partharbor/model-formats";

const number = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

const sampleBox = `solid PartHarbor_orientation_sample
facet normal 0 0 -1
outer loop
vertex 0 0 0
vertex 0 10 0
vertex 20 10 0
endloop
endfacet
facet normal 0 0 -1
outer loop
vertex 0 0 0
vertex 20 10 0
vertex 20 0 0
endloop
endfacet
facet normal 0 0 1
outer loop
vertex 0 0 5
vertex 20 0 5
vertex 20 10 5
endloop
endfacet
facet normal 0 0 1
outer loop
vertex 0 0 5
vertex 20 10 5
vertex 0 10 5
endloop
endfacet
facet normal 0 -1 0
outer loop
vertex 0 0 0
vertex 20 0 0
vertex 20 0 5
endloop
endfacet
facet normal 0 -1 0
outer loop
vertex 0 0 0
vertex 20 0 5
vertex 0 0 5
endloop
endfacet
facet normal 0 1 0
outer loop
vertex 0 10 0
vertex 0 10 5
vertex 20 10 5
endloop
endfacet
facet normal 0 1 0
outer loop
vertex 0 10 0
vertex 20 10 5
vertex 20 10 0
endloop
endfacet
facet normal -1 0 0
outer loop
vertex 0 0 0
vertex 0 0 5
vertex 0 10 5
endloop
endfacet
facet normal -1 0 0
outer loop
vertex 0 0 0
vertex 0 10 5
vertex 0 10 0
endloop
endfacet
facet normal 1 0 0
outer loop
vertex 20 0 0
vertex 20 10 0
vertex 20 10 5
endloop
endfacet
facet normal 1 0 0
outer loop
vertex 20 0 0
vertex 20 10 5
vertex 20 0 5
endloop
endfacet
endsolid PartHarbor_orientation_sample`;

export function AxisOrientationTool() {
  const [file, setFile] = useState<File>(), [threshold, setThreshold] = useState(45), [report, setReport] = useState<AxisOrientationAnalysis>(), [message, setMessage] = useState("Choose an STL up to 25 MiB."), [running, setRunning] = useState(false);
  const worker = useRef<Worker | null>(null), timeout = useRef<number | undefined>(undefined);
  const stop = () => { worker.current?.terminate(); worker.current = null; if (timeout.current) clearTimeout(timeout.current); timeout.current = undefined; setRunning(false); };
  useEffect(() => () => { worker.current?.terminate(); if (timeout.current) clearTimeout(timeout.current); }, []);
  const run = async () => {
    if (!file) { setMessage("Choose an STL first."); return; } if (file.size > 25 * 1024 * 1024) { setMessage("Rejected: STL exceeds the 25 MiB orientation-analysis limit."); return; }
    stop(); setReport(undefined); setRunning(true); setMessage("Comparing six axis-aligned build directions in an isolated local worker…");
    try {
      const buffer = await file.arrayBuffer(), current = new Worker(new URL("./orientation.worker.ts", import.meta.url), { type: "module" }); worker.current = current;
      timeout.current = window.setTimeout(() => { stop(); setMessage("Stopped after the 30-second local worker timeout."); }, 30_000);
      current.onmessage = (event: MessageEvent<{ ok: boolean; result?: AxisOrientationAnalysis; error?: string }>) => { if (event.data.ok && event.data.result) { setReport(event.data.result); setMessage("Complete. Rankings are advisory and the source STL was not changed."); } else setMessage(event.data.error ?? "Orientation worker returned an invalid result."); stop(); };
      current.onerror = () => { stop(); setMessage("The isolated orientation worker stopped unexpectedly."); };
      current.postMessage({ id: `${Date.now()}-${Math.random()}`, buffer, threshold }, [buffer]);
    } catch (error) {
      stop(); setMessage(error instanceof Error ? `Could not read the STL: ${error.message}` : "Could not read the selected STL.");
    }
  };
  const loadSample = () => { const sample = new File([sampleBox], "orientation-sample-20x10x5.stl", { type: "model/stl" }); setFile(sample); setReport(undefined); setMessage("Built-in 20 × 10 × 5 box ready for six-direction analysis."); };
  return <div><label className="dropzone"><input type="file" accept=".stl,model/stl" disabled={running} onChange={(event) => { const next = event.target.files?.[0]; setFile(next); setReport(undefined); setMessage(next ? `${(next.size / 1024).toFixed(1)} KiB ready for six-direction analysis.` : "Choose an STL up to 25 MiB."); }}/><strong>{file ? "SOURCE STL READY" : "CHOOSE A LOCAL STL"}</strong><span>{message}</span></label><div className="orientation-controls"><label className="field"><span>Overhang threshold from vertical</span><div><input type="number" min="0" max="89" step="1" value={threshold} onChange={(event) => { setThreshold(Number(event.target.value)); setReport(undefined); }}/><b>°</b></div></label><div className="action-row"><button className="action" type="button" disabled={running} onClick={loadSample}>USE 20×10×5 SAMPLE</button><button className="action" type="button" disabled={!file || running || threshold < 0 || threshold >= 90} onClick={() => void run()}>RANK SIX ORIENTATIONS</button>{running && <button className="action action--danger" type="button" onClick={() => { stop(); setMessage("Cancelled. No report was retained."); }}>CANCEL</button>}</div></div>{report && <div className="orientation-report"><div className="gcode-verdict"><span>LOWEST ESTIMATED DOWNWARD AREA</span><strong>{report.candidates[0].up} UP</strong><p>{number.format(report.candidates[0].estimatedOverhangArea)} units² · {number.format(report.candidates[0].estimatedOverhangPercent)}% of triangulated surface · {number.format(report.candidates[0].height)} units tall</p></div><div className="orientation-table" role="table" aria-label="Axis orientation ranking"><div role="row"><b role="columnheader">Rank</b><b role="columnheader">Up direction</b><b role="columnheader">Footprint</b><b role="columnheader">Height</b><b role="columnheader">Downward area</b><b role="columnheader">Surface %</b></div>{report.candidates.map((candidate, index) => <div role="row" key={candidate.up}><span role="cell">{index + 1}</span><strong role="cell">{candidate.up}</strong><span role="cell">{candidate.footprint.map((value) => number.format(value)).join(" × ")}</span><span role="cell">{number.format(candidate.height)}</span><span role="cell">{number.format(candidate.estimatedOverhangArea)} units²</span><span role="cell">{number.format(candidate.estimatedOverhangPercent)}%</span></div>)}</div><p className="safe-subset-note">Ranking uses triangle winding and axis-aligned downward-facing area, then height and bounding-box footprint. It does not simulate supports, bridges, bed contact, stability, strength, surface finish, anisotropy, trapped volume, or arbitrary-angle rotation. Repair inconsistent normals before trusting the comparison.</p></div>}</div>;
}
